<template>
  <div
      class="ub"
      :class="{ drag: dragOver, disabled }"
      @click="openPicker"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      role="button"
      tabindex="0"
      @keydown.enter.prevent="openPicker"
      @keydown.space.prevent="openPicker"
      :aria-disabled="disabled"
  >
    <input
        ref="fileInput"
        class="ub-input"
        type="file"
        multiple
        :accept="accept"
        :disabled="disabled"
        @change="onPick"
    />

    <div class="ub-inner">
      <div class="ub-title">+ Bilder auswählen</div>
      <div class="ub-sub">oder hierher ziehen & ablegen</div>
      <div class="ub-meta">
        {{ hint }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { ACCEPT_IMAGE_INPUT, isAcceptedImageUploadFile, MAX_IMAGE_UPLOAD_MB } from '../../../utils/fileAccept'
import { createEditorPreview, createThumb } from '../../../utils/imageOps'

type UploadItem = {
  id: string
  productId: number | null
  categoryId: number | null
  blob: Blob
  fileName: string
  previewUrl: string
  quantity: number
  size: any | null
  metaData: Record<string, any>
}

const props = defineProps<{
  productId: number
  categoryId: number
  size?: { id: string; name?: string; width: number; height: number } | null
  disabled?: boolean
  maxSizeMB?: number
  accept?: string
}>()

const emit = defineEmits<{
  (e: 'uploaded', images: UploadItem[]): void
}>()

const disabled = computed(() => !!props.disabled)
const maxSizeMB = computed(() => (Number(props.maxSizeMB) > 0 ? Number(props.maxSizeMB) : MAX_IMAGE_UPLOAD_MB))
const accept = computed(() => props.accept ?? ACCEPT_IMAGE_INPUT)

const hint = computed(() => `Max. ${maxSizeMB.value} MB · JPG/PNG/TIFF/WEBP`)

const fileInput = ref<HTMLInputElement | null>(null)
const dragOver = ref(false)

function openPicker() {
  if (disabled.value) return
  fileInput.value?.click()
}

function onDragOver() {
  if (disabled.value) return
  dragOver.value = true
}
function onDragLeave() {
  dragOver.value = false
}

function validateFile(file: File) {
  const maxBytes = maxSizeMB.value * 1024 * 1024
  return isAcceptedImageUploadFile(file, maxBytes)
}

async function buildPreviewUrl(file: File): Promise<string> {
  const preview = await createEditorPreview(file, 1600, 'image/webp', 0.82).catch(() => null)
  if (preview?.blob) return URL.createObjectURL(preview.blob)

  const thumb = await createThumb(file, 560, 'image/jpeg', 0.86).catch(() => null)
  if (thumb?.blob) return URL.createObjectURL(thumb.blob)

  return URL.createObjectURL(file)
}

function buildImmediatePreviewUrl(file: File): string {
  try {
    return URL.createObjectURL(file)
  } catch {
    return ''
  }
}

const previewUpgradeQueue: Array<() => Promise<void>> = []
let previewUpgradeActive = 0
const PREVIEW_UPGRADE_CONCURRENCY = 3

function pumpPreviewUpgradeQueue() {
  while (previewUpgradeActive < PREVIEW_UPGRADE_CONCURRENCY && previewUpgradeQueue.length > 0) {
    const task = previewUpgradeQueue.shift()
    if (!task) return
    previewUpgradeActive += 1
    void task().finally(() => {
      previewUpgradeActive = Math.max(0, previewUpgradeActive - 1)
      pumpPreviewUpgradeQueue()
    })
  }
}

function enqueuePreviewUpgrade(task: () => Promise<void>) {
  previewUpgradeQueue.push(task)
  pumpPreviewUpgradeQueue()
}

async function upgradeItemPreview(item: UploadItem, file: File, items: UploadItem[]) {
  const nextUrl = await buildPreviewUrl(file).catch(() => '')
  if (!nextUrl) return
  const target = items.find((entry) => entry.id === item.id)
  if (!target) {
    if (nextUrl.startsWith('blob:')) {
      try { URL.revokeObjectURL(nextUrl) } catch {}
    }
    return
  }
  const prevUrl = String(target.previewUrl || '').trim()
  if (prevUrl === nextUrl) return
  target.previewUrl = nextUrl
  if (prevUrl.startsWith('blob:')) {
    try { URL.revokeObjectURL(prevUrl) } catch {}
  }
}

async function buildItems(files: FileList | File[]) {
  const out: UploadItem[] = []
  for (const file of Array.from(files)) {
    if (!validateFile(file)) continue
    const id = uuidv4()
    const item: UploadItem = {
      id,
      productId: props.productId ?? null,
      categoryId: props.categoryId ?? null,
      blob: file,
      fileName: file.name,
      previewUrl: buildImmediatePreviewUrl(file),
      quantity: 1,
      size: props.size ?? null,
      metaData: {},
    }
    out.push(item)
    enqueuePreviewUpgrade(() => upgradeItemPreview(item, file, out))
  }
  return out
}

async function onPick(e: Event) {
  const files = (e.target as HTMLInputElement).files
  if (!files?.length) return
  const items = await buildItems(files)
  emit('uploaded', items)

  // reset input (damit gleiche Datei erneut auswählbar ist)
  ;(e.target as HTMLInputElement).value = ''
}

async function onDrop(e: DragEvent) {
  dragOver.value = false
  if (disabled.value) return
  const files = e.dataTransfer?.files
  if (!files?.length) return
  const items = await buildItems(files)
  emit('uploaded', items)
}

async function handleFiles(files: FileList | File[]) {
  if (disabled.value) return
  const items = await buildItems(files)
  if (items.length) {
    emit('uploaded', items)
  }
}

defineExpose({ openPicker, handleFiles, dragOver })
</script>

<style scoped lang="scss">
.ub {
  border: 0;
  border-radius: 0;
  padding: 16px;
  min-height: 140px;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.08s ease;
  user-select: none;
  outline: none;
}

.ub:hover {
  background: rgba(15, 23, 42, 0.035);
  border-bottom-color: rgba(15, 23, 42, 0.26);
}

.ub:active {
  transform: translateY(1px);
}

.ub.drag {
  background: rgba(239, 68, 68, 0.06);
  border-bottom-color: rgba(239, 68, 68, 0.55);
}

.ub.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.ub-input {
  display: none;
}

.ub-inner {
  text-align: center;
  display: grid;
  gap: 6px;
}

.ub-title {
  font-weight: 700;
  color: rgba(15, 23, 42, 0.88);
  font-size: 14px;
}

.ub-sub {
  font-weight: 700;
  color: rgba(15, 23, 42, 0.52);
  font-size: 12px;
}

.ub-meta {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.45);
}
</style>
